import { Check, LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { formatDateTime, formatDuration } from "../../lib/format";
import type { SvgJob } from "../../lib/types";
import { getJobPhases, getTimingSummary, type PhaseState } from "./timing";
import { getProgress, getStageLabel, isTerminal } from "./status";

function PhaseIcon({ state, index }: { state: PhaseState; index: number }) {
  return (
    <span className={`phase-icon phase-${state}`}>
      {state === "complete" && <Check size={15} />}
      {state === "active" && <LoaderCircle className="spin" size={15} />}
      {state === "error" && <X size={15} />}
      {state === "pending" && index + 1}
    </span>
  );
}

export function JobProgress({ job, compact = false }: { job: SvgJob; compact?: boolean }) {
  const terminal = isTerminal(job);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
    if (terminal) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [job.id, terminal]);

  const progress = getProgress(job);
  const stage = getStageLabel(job);
  const timing = getTimingSummary(job, now);
  const phases = getJobPhases(job, now);

  if (compact) {
    return (
      <div className="compact-progress" aria-label={`${stage} ${progress}%`}>
        <div className="compact-progress-label"><span>{stage}</span><strong>{progress}%</strong></div>
        <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
      </div>
    );
  }

  return (
    <div className="job-progress">
      <div className="progress-summary">
        <div>
          <span className="meta-label">当前阶段</span>
          <strong className="progress-stage">{stage}</strong>
          <div className="progress-track large" role="progressbar" aria-label="任务进度" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
            <span style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="progress-numbers">
          <div><span>进度</span><strong>{progress}%</strong></div>
          <div><span>{timing.label}</span><strong>{formatDuration(timing.milliseconds)}</strong></div>
        </div>
      </div>

      <ol className="phase-list" aria-label="SVG 构建流程">
        {phases.map((phase, index) => (
          <li key={phase.key} className={`phase-item phase-${phase.state}`}>
            <div className="phase-line-row">
              <span className="phase-line" />
              <PhaseIcon state={phase.state} index={index} />
              <span className="phase-line" />
            </div>
            <strong>{phase.label}</strong>
            <span>{formatDuration(phase.elapsedMilliseconds)}</span>
          </li>
        ))}
      </ol>

      <details className="progress-details">
        <summary>查看阶段时间与技术状态</summary>
        <div className="milestone-grid">
          {phases.map((phase) => (
            <article key={phase.key}>
              <div><strong>{phase.label}</strong><span className={`milestone-dot phase-${phase.state}`} /></div>
              <p>{phase.at ? formatDateTime(phase.at) : phase.state === "active" ? "正在执行" : "等待执行"}</p>
              <small>{formatDuration(phase.elapsedMilliseconds)}</small>
            </article>
          ))}
        </div>
        <div className="technical-row"><span>阶段标识：<code>{job.progress_stage || job.progress}</code></span><span>最近更新：{formatDateTime(job.updated_at)}</span></div>
      </details>
    </div>
  );
}
